import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClipboardList } from "lucide-react";
import { toast } from "sonner";

export default function Intake() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({ exam_date: "", state: "California", license_type: "Property & Casualty", daily_goal_minutes: 30, experience: "new" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/intake", form);
      await refresh();
      toast.success("You're all set!");
      navigate("/dashboard");
    } catch {
      toast.error("Could not save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-xl bg-[#0A192F] flex items-center justify-center mx-auto mb-4"><ClipboardList className="h-6 w-6 text-amber-500" /></div>
          <h1 className="font-heading text-2xl font-bold text-[#0A192F]">Let's personalize your prep</h1>
          <p className="text-slate-500 mt-1 text-sm">A few quick questions to set up your study dashboard.</p>
        </div>
        <form onSubmit={submit} className="space-y-5 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
          <div>
            <Label>Target exam date</Label>
            <Input type="date" value={form.exam_date} onChange={(e) => setForm({ ...form, exam_date: e.target.value })} className="mt-1.5" data-testid="intake-exam-date" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>State</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger className="mt-1.5" data-testid="intake-state"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="California">California</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>License type</Label>
              <Select value={form.license_type} onValueChange={(v) => setForm({ ...form, license_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="intake-license"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Property & Casualty">Property &amp; Casualty</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Experience level</Label>
            <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
              <SelectTrigger className="mt-1.5" data-testid="intake-experience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New to insurance</SelectItem>
                <SelectItem value="some">Some experience</SelectItem>
                <SelectItem value="renewing">Renewing / re-licensing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Daily study goal: {form.daily_goal_minutes} min</Label>
            <input type="range" min={10} max={120} step={5} value={form.daily_goal_minutes} onChange={(e) => setForm({ ...form, daily_goal_minutes: Number(e.target.value) })} className="w-full mt-3 accent-amber-600" data-testid="intake-goal" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-amber-600 hover:bg-amber-700 text-white h-11" data-testid="intake-submit-btn">
            {loading ? "Saving…" : "Go to my dashboard"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
